import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuCircleCheck, LuListOrdered, LuSearch, LuSearchX, LuVolume2, LuX } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CHAPTER_ICONS, GROUPS } from './chapters';
import { formatClock } from './engine/usePlayback';
import { searchGuide } from './guideSearch';

const STEP_BASE = 'tw-flex tw-w-full tw-items-start tw-gap-2 tw-rounded-xl tw-px-2 tw-py-1.5 tw-text-left tw-text-[13px] tw-leading-snug tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50';

/** One step of a chapter: its number and its full caption (the written guide). */
const StepButton = ({ beat, number, current, clamp, onSelect }) => (
  <li>
    <button
      type="button"
      onClick={() => onSelect(beat)}
      aria-current={current ? 'step' : undefined}
      className={`${STEP_BASE} ${current ? 'tw-bg-teal-500/10 tw-font-medium tw-text-teal-900 dark:tw-text-teal-100' : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.04] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.05]'}`}
    >
      <span className={`tw-mt-px tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-[10px] tw-font-bold ${current ? 'tw-bg-teal-500 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300'}`}>
        {current ? <LuVolume2 aria-hidden="true" className="tw-h-3 tw-w-3" /> : number}
      </span>
      <span className={clamp ? 'tw-line-clamp-2' : ''}>{beat.caption}</span>
    </button>
  </li>
);

StepButton.propTypes = { beat: PropTypes.object.isRequired, number: PropTypes.number.isRequired, current: PropTypes.bool, clamp: PropTypes.bool, onSelect: PropTypes.func.isRequired };

/** A topic row: icon, title, length or a watched tick, and its progress while it plays. */
const ChapterButton = ({ chapter, active, watched, share, onSelect }) => {
  const { t } = useTranslation();
  const Icon = CHAPTER_ICONS[chapter.key];
  return (
    <button
      type="button"
      onClick={() => onSelect(chapter)}
      aria-current={active ? 'true' : undefined}
      className={`tw-w-full tw-rounded-2xl tw-border tw-px-3 tw-py-2 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
        active ? 'tw-border-teal-500/40 tw-bg-teal-500/10' : 'tw-border-transparent hover:tw-border-slate-200/80 hover:tw-bg-white dark:hover:tw-border-white/10 dark:hover:tw-bg-white/[0.05]'
      }`}
    >
      <span className="tw-flex tw-items-center tw-gap-2.5">
        <span className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg ${active ? 'tw-bg-teal-500 tw-text-white' : 'tw-bg-slate-900/[0.05] tw-text-teal-700 dark:tw-bg-white/[0.07] dark:tw-text-teal-300'}`}>
          <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
        </span>
        <span className={`tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm ${active ? 'tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200'}`}>
          {t(`userGuide.chapters.${chapter.key}`)}
        </span>
        {watched && !active
          ? <LuCircleCheck aria-label={t('userGuide.player.watched')} className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-500" />
          : <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-slate-400">{formatClock(chapter.end - chapter.start)}</span>}
      </span>
      {active && (
        <span className="tw-mt-2 tw-block tw-h-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
          <span className="tw-block tw-h-full tw-rounded-full tw-bg-teal-500" style={{ width: `${share}%` }} />
        </span>
      )}
    </button>
  );
};

ChapterButton.propTypes = { chapter: PropTypes.object.isRequired, active: PropTypes.bool, watched: PropTypes.bool, share: PropTypes.number, onSelect: PropTypes.func.isRequired };

/**
 * Index of the guide: a search box and the topics by group. The playing
 * topic opens to show its steps; search results list the matching steps.
 * On phones the list folds under a toggle (it opens while searching).
 */
const GuideIndex = ({ timeline, time, chapterIndex, beatIndex, watched, query, onQuery, open, onToggle, onChapter, onBeat }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`userGuide.player.${key}`, vars);
  const activeRef = useRef(null);
  const results = searchGuide(timeline, t, query);
  const current = timeline.chapters[chapterIndex];
  const share = Math.min(100, Math.max(0, ((time - current.start) / (current.end - current.start)) * 100));

  // Keep the playing topic in view as the video moves on (desktop only: on a
  // phone the list sits above the video and must not pull the page away)
  useEffect(() => {
    if (query || !window.matchMedia?.('(min-width: 1024px)').matches) return;
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [chapterIndex, query]);

  const chapterList = (chapter) => {
    const active = chapter.key === current.key;
    return (
      <li key={chapter.key} ref={active ? activeRef : undefined}>
        <ChapterButton chapter={chapter} active={active} watched={watched.includes(chapter.key)} share={share} onSelect={onChapter} />
        {active && chapter.beats.length > 1 && (
          <ol aria-label={label('steps')} className="tw-mb-1 tw-ml-4 tw-mt-1 tw-space-y-0.5 tw-border-l-2 tw-border-teal-500/25 tw-pl-2">
            {chapter.beats.map((beat, number) => (
              <StepButton key={beat.id} beat={beat} number={number + 1} current={beat.index === beatIndex} onSelect={onBeat} />
            ))}
          </ol>
        )}
      </li>
    );
  };

  let list;
  if (results && results.length === 0) {
    list = (
      <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-bg-slate-900/[0.04] tw-px-3 tw-py-3 tw-text-sm tw-text-slate-600 dark:tw-bg-white/[0.04] dark:tw-text-slate-300">
        <LuSearchX aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
        {label('noResults', { term: query })}
      </p>
    );
  } else if (results) {
    list = (
      <ul className="tw-space-y-1.5">
        {results.map(({ chapter, beats }) => (
          <li key={chapter.key}>
            <ChapterButton chapter={chapter} active={chapter.key === current.key} watched={watched.includes(chapter.key)} share={share} onSelect={onChapter} />
            {beats.length > 0 && (
              <>
                <p className="tw-ml-4 tw-mt-1 tw-pl-2 tw-text-[11px] tw-font-semibold tw-text-slate-400">{label('matchedSteps', { count: beats.length })}</p>
                <ol className="tw-mb-1 tw-ml-4 tw-space-y-0.5 tw-border-l-2 tw-border-teal-500/25 tw-pl-2">
                  {beats.map((beat) => (
                    <StepButton key={beat.id} beat={beat} number={chapter.beats.indexOf(beat) + 1} current={beat.index === beatIndex} clamp onSelect={onBeat} />
                  ))}
                </ol>
              </>
            )}
          </li>
        ))}
      </ul>
    );
  } else {
    list = (
      <div className="tw-space-y-3">
        {GROUPS.map((group) => (
          <section key={group.key} aria-label={t(`userGuide.groups.${group.key}`)}>
            <p className="tw-px-1 tw-pb-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{t(`userGuide.groups.${group.key}`)}</p>
            <ul className="tw-space-y-0.5">{group.chapters.map((key) => chapterList(timeline.chapters.find((chapter) => chapter.key === key)))}</ul>
          </section>
        ))}
      </div>
    );
  }

  return (
    <aside aria-label={label('topics')} className="tw-min-w-0 tw-space-y-3 lg:tw-sticky lg:tw-top-0 lg:tw-flex lg:tw-max-h-[calc(88vh-8.5rem)] lg:tw-flex-col lg:tw-space-y-0 lg:tw-gap-3 lg:tw-self-start">
      <label className="tw-relative tw-block lg:tw-shrink-0">
        <span className="tw-sr-only">{label('searchLabel')}</span>
        <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={label('searchPlaceholder')}
          className="tw-h-11 tw-w-full tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-pl-10 tw-pr-10 tw-text-sm tw-text-slate-800 tw-shadow-sm tw-outline-none tw-transition focus:tw-border-teal-500 focus:tw-ring-2 focus:tw-ring-teal-500/30 dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-100"
        />
        {query && (
          <button type="button" onClick={() => onQuery('')} aria-label={t('common.clearSearch')} className="tw-absolute tw-right-2 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10">
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </button>
        )}
      </label>

      {!query && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-200 lg:tw-hidden"
        >
          <LuListOrdered aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" />
          <span className="tw-min-w-0 tw-flex-1 tw-truncate">{open ? label('hideTopics') : `${label('showTopics')} · ${t(`userGuide.chapters.${current.key}`)}`}</span>
          <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`} />
        </button>
      )}

      <div className={`glass-scrollbar lg:tw-min-h-0 lg:tw-flex-1 lg:tw-overflow-y-auto lg:tw-pr-1 ${open || query ? '' : 'tw-hidden lg:tw-block'}`}>{list}</div>
    </aside>
  );
};

GuideIndex.propTypes = {
  timeline: PropTypes.object.isRequired,
  time: PropTypes.number.isRequired,
  chapterIndex: PropTypes.number.isRequired,
  beatIndex: PropTypes.number.isRequired,
  watched: PropTypes.arrayOf(PropTypes.string).isRequired,
  query: PropTypes.string.isRequired,
  onQuery: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChapter: PropTypes.func.isRequired,
  onBeat: PropTypes.func.isRequired,
};

export default GuideIndex;
